import React, { useState, useEffect, useCallback } from 'react'
import {
  CCard, CCardBody, CButton, CCardHeader, CCol,
  CRow, CModal, CModalHeader, CModalBody,
  CForm, CFormGroup, CFormText,
  CLabel, CInput, CBadge,
} from 'compat/coreui';
import { toast } from 'react-toastify';
import axios from 'axios';

const API = process.env.REACT_APP_APP_API_URL;
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

const emptyForm = { name: '', description: '', brand: '', sku: '', price: '', stock: '', status: 'active', preOrder: 1, category: '' };

const MenuProducts = () => {
  const [modal, setModal] = useState(false);          // add/edit product
  const [categoryModal, setCategoryModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);         // null = adding
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [title, setTitle] = useState('');
  const [search, setSearch] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const getCategories = useCallback(() => {
    axios.get(`${API}/api/category?menu=1`, { headers: authHeaders() })
      .then(res => setCategoryData(res.data.list || []))
      .catch(() => {});
  }, []);

  const getProducts = useCallback(() => {
    axios.get(`${API}/api/menuitem`, { headers: authHeaders() })
      .then(res => setProductsData(res.data.list || []))
      .catch(() => {});
  }, []);

  useEffect(() => { getCategories(); getProducts(); }, [getCategories, getProducts]);

  const openAdd = (categoryId) => {
    setEditId(null);
    setImageFile(null);
    setForm({ ...emptyForm, category: categoryId || (categoryData[0] && categoryData[0]._id) || '' });
    setModal(true);
  };

  const openEdit = (p) => {
    setEditId(p._id);
    setImageFile(null);
    setForm({
      name: p.name || '', description: p.description || '', brand: p.brand || '',
      sku: p.sku || '', price: p.price || '', stock: p.stock ?? '',
      status: p.status || 'active', preOrder: p.preOrder || 1,
      category: (p.category && p.category._id) || p.category || '',
    });
    setModal(true);
  };

  const uploadImage = async (productId) => {
    if (!imageFile || !productId) return;
    const fd = new FormData();
    fd.append('productId', productId);
    fd.append('photos', imageFile);
    await axios.post(`${API}/api/productimage`, fd, { headers: authHeaders() });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.description || !form.price || !form.category) {
      toast.warn('Name, category, description, and price are required');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const res = await axios.post(`${API}/api/editmenuitem`, { itemId: editId, ...form }, { headers: authHeaders() });
        if (!res.data.status) throw new Error(res.data.message);
        await uploadImage(editId);
        toast.success('Product updated');
      } else {
        const res = await axios.post(`${API}/api/menuitem`, form, { headers: authHeaders() });
        if (!res.data.status) throw new Error(res.data.message);
        await uploadImage(res.data.menuItem && res.data.menuItem._id);
        toast.success('Product added');
      }
      setModal(false);
      getProducts();
    } catch (err) {
      toast.error((err && err.message) || 'Could not save the product');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = (e) => {
    e.preventDefault();
    if (!title) return;
    axios.post(`${API}/api/category`, { parent: null, title, subTitle: '', image: null, isMenu: 1 }, { headers: authHeaders() })
      .then(response => {
        if (response.data.status) {
          getCategories();
          setTitle('');
          setCategoryModal(false);
        }
      }).catch(() => toast.error('Could not add the category'))
  };

  const removeCategory = (id) => {
    if (!window.confirm('Delete this category?')) return;
    axios.get(`${API}/api/category/remove?categoryId=${id}`, { headers: authHeaders() })
      .then(res => setCategoryData(res.data.list || []))
      .catch(() => {});
  };

  const copyProduct = (p) => {
    axios.get(`${API}/api/category/copy?categoryId=${p._id}`, { headers: authHeaders() })
      .then(() => { getProducts(); toast.success('Product duplicated'); })
      .catch(() => {});
  };

  const matchesSearch = (p) => !search ||
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase());

  return (
    <CRow className="menu_product_page">
      <CCol xl={12}>
        <CCard>
          <CCardHeader className="category_header">
            Products
            <div className="category_header_search">
              <CForm inline onSubmit={e => e.preventDefault()}>
                <CInput
                  className="mr-sm-2"
                  placeholder="Search name, brand, SKU…"
                  size="sm"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </CForm>
              <CButton onClick={() => setCategoryModal(true)} className="mr-1" color="success">Add Category</CButton>
              <CButton onClick={() => openAdd()} color="success">Add Product</CButton>
            </div>
          </CCardHeader>
          <CCardBody>
            {categoryData.length === 0 && (
              <p className="text-muted">Create a category first, then add products to it.</p>
            )}
            {categoryData.map((item) => (
              <div key={item._id} className="mb-4">
                <CRow className="mt-1 pt-1 pb-1 mb-2 align-items-center">
                  <CCol lg={4} className="d-flex align-items-center">
                    <h4 className="mb-0">{item.title}</h4>
                  </CCol>
                  <CCol lg={8} className="product-add-btn text-right">
                    <span onClick={() => openAdd(item._id)}><i className="fa fa-plus-circle circle"></i> Add Product</span>
                    <span onClick={() => removeCategory(item._id)}><i className="fa fa-times-circle"></i></span>
                  </CCol>
                </CRow>

                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '34%' }}>Product</th>
                        <th>Brand</th>
                        <th>SKU</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productsData
                        .filter(p => p.category && (p.category._id || p.category) === item._id)
                        .filter(matchesSearch)
                        .map((p) => (
                          <tr key={p._id}>
                            <td>
                              <strong>{p.name}</strong>
                              <div className="small text-muted" style={{ maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.description}</div>
                            </td>
                            <td>{p.brand || '—'}</td>
                            <td>{p.sku || '—'}</td>
                            <td><strong>${p.price}</strong></td>
                            <td>
                              {p.stock === null || p.stock === undefined ? <span className="text-muted">untracked</span>
                                : p.stock <= 0 ? <CBadge color="danger">out of stock</CBadge>
                                : p.stock < 10 ? <CBadge color="warning">{p.stock} left</CBadge>
                                : p.stock}
                            </td>
                            <td>
                              <CBadge color={p.status === 'active' ? 'success' : 'secondary'}>{p.status}</CBadge>
                              {p.preOrder === 2 && <CBadge color="info" className="ml-1">in-store only</CBadge>}
                            </td>
                            <td className="text-right menu-add-btn">
                              <span onClick={() => openEdit(p)}><i className="fa fa-pencil"></i></span>
                              <span onClick={() => copyProduct(p)}><i className="fa fa-copy"></i></span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </CCardBody>
        </CCard>
      </CCol>

      <CModal show={modal} onClose={() => setModal(false)}>
        <CModalHeader closeButton>{editId ? 'Edit Product' : 'Add Product'}</CModalHeader>
        <CModalBody>
          <form onSubmit={saveProduct}>
            <CFormGroup className="row px-4">
              <CCol xl={6} md={6} className="pl-0">
                <CLabel>Category *</CLabel>
                <select className="custom-select my-1 mr-sm-2 product_status" onChange={set('category')} value={form.category}>
                  {categoryData.map((cat) => (
                    <option key={cat._id} value={cat._id}>{cat.title}</option>
                  ))}
                </select>
              </CCol>
              <CCol xl={6} md={6} className="pr-0">
                <CLabel>Product Name *</CLabel>
                <CInput type="text" placeholder="e.g. Wireless Earbuds Pro" value={form.name} onChange={set('name')} />
              </CCol>
            </CFormGroup>
            <CFormGroup>
              <CLabel>Description *</CLabel>
              <CInput type="text" placeholder="Short product description…" value={form.description} onChange={set('description')} />
            </CFormGroup>
            <CFormGroup className="row px-4">
              <CCol xl={6} md={6} className="pl-0">
                <CLabel>Brand</CLabel>
                <CInput type="text" placeholder="Brand name" value={form.brand} onChange={set('brand')} />
              </CCol>
              <CCol xl={6} md={6} className="pr-0">
                <CLabel>SKU</CLabel>
                <CInput type="text" placeholder="Internal SKU / code" value={form.sku} onChange={set('sku')} />
              </CCol>
            </CFormGroup>
            <CFormGroup className="row px-4">
              <CCol xl={6} md={6} className="pl-0">
                <CLabel>Price (USD) *</CLabel>
                <CInput type="number" min="0" step="0.01" placeholder="0.00" value={form.price} onChange={set('price')} />
              </CCol>
              <CCol xl={6} md={6} className="pr-0">
                <CLabel>Stock</CLabel>
                <CInput type="number" min="0" step="1" placeholder="Leave empty for untracked" value={form.stock} onChange={set('stock')} />
              </CCol>
            </CFormGroup>
            <CFormGroup>
              <CLabel>Product Photo</CLabel>
              <div className="fileUpload">
                <span>{imageFile ? imageFile.name : 'Upload a Photo'}</span>
                <CInput type="file" accept="image/*" className="upload" name="photos" onChange={(e) => setImageFile(e.target.files[0])} />
              </div>
              <CFormText className="help-block">JPEG / PNG / GIF</CFormText>
            </CFormGroup>
            <CFormGroup className="row px-4">
              <CCol xl={6} md={6} className="pl-0">
                <CLabel>Status</CLabel>
                <select className="custom-select my-1 mr-sm-2 product_status" value={form.status} onChange={set('status')}>
                  <option value="active">Active</option>
                  <option value="disable">Inactive</option>
                </select>
              </CCol>
              <CCol xl={6} md={6} className="pr-0">
                <CLabel>Online Ordering</CLabel>
                <select className="custom-select my-1 mr-sm-2 product_status" value={form.preOrder} onChange={e => setForm(f => ({ ...f, preOrder: parseInt(e.target.value, 10) }))}>
                  <option value={1}>Available online</option>
                  <option value={2}>In-store only</option>
                </select>
              </CCol>
            </CFormGroup>
            <CButton color="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</CButton>{' '}
            <CButton color="secondary" onClick={() => setModal(false)}>Cancel</CButton>
          </form>
        </CModalBody>
      </CModal>

      <CModal show={categoryModal} onClose={() => setCategoryModal(false)}>
        <CModalHeader closeButton>Add Category</CModalHeader>
        <CModalBody>
          <form onSubmit={addCategory}>
            <CFormGroup>
              <CLabel>Category Name</CLabel>
              <CInput
                type="text"
                placeholder="e.g. Electronics, Fashion, Grocery…"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
              <CFormText className="help-block">Products are grouped by category in your store</CFormText>
            </CFormGroup>
            <CButton color="primary" type="submit">Add</CButton>{' '}
            <CButton color="secondary" onClick={() => setCategoryModal(false)}>Cancel</CButton>
          </form>
        </CModalBody>
      </CModal>
    </CRow>
  )
}

export default MenuProducts;
